import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  ArrowLeftIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  DocumentIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ClipboardIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  InboxIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentCheckIcon,
  RocketLaunchIcon,
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  IdentificationIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import dayjs from "dayjs";
import clsx from "clsx";
import Swal from "sweetalert2";
import { BASE_URL } from "../../config";
import MemberViewModal from "../Member/MemberViewModal";

const formatDate = (d, withTime = false) => {
  if (!d) return "N/A";
  const m = dayjs(d);
  if (!m.isValid()) return d;
  return withTime ? m.format("MMM D, YYYY h:mm A") : m.format("MMM D, YYYY");
};
const formatCurrency = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(n);
};

const formatFundUtilPercent = (revolvingFund, remainingFund) => {
  const revolving = Number(revolvingFund);
  const remaining = Number(remainingFund);

  if (
    !Number.isFinite(revolving) ||
    revolving === 0 ||
    !Number.isFinite(remaining)
  ) {
    return "N/A";
  }

  return `${(((revolving - remaining) / revolving) * 100).toFixed(2)}%`;
};

const parseDateInput = (value) => {
  if (!value) return null;

  const [year, month, day] = String(value)
    .slice(0, 10)
    .split("-")
    .map((part) => Number(part));

  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addMonthsWithRollover = (baseDate, monthsToAdd) => {
  const target = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate()
  );

  target.setMonth(target.getMonth() + monthsToAdd);

  return target;
};

const deriveExpiryDuration = (effectiveDate, expiryDate) => {
  if (!effectiveDate || !expiryDate) return "y";

  return new Date(expiryDate) - new Date(effectiveDate) >
    180 * 24 * 60 * 60 * 1000
    ? "y"
    : "m";
};

const getPolicyPeriod = (data) => {
  const effectiveDate = parseDateInput(data?.effective_date);
  const savedExpiryDate = parseDateInput(data?.expiry_date);
  const expiryDuration = deriveExpiryDuration(
    data?.effective_date,
    data?.expiry_date
  );

  if (!effectiveDate) {
    return {
      effectiveDateFrom: data?.effective_date,
      effectiveDateTo: data?.effective_date_to,
      expiryDuration,
      expiryDate: data?.expiry_date,
    };
  }

  const monthsToAdd = expiryDuration === "m" ? 6 : 12;
  const fallbackExpiryDate = addMonthsWithRollover(effectiveDate, monthsToAdd);
  const expiryDate = savedExpiryDate || fallbackExpiryDate;
  const effectiveDateTo = new Date(expiryDate);
  effectiveDateTo.setDate(effectiveDateTo.getDate() - 1);

  return {
    effectiveDateFrom: data?.effective_date,
    effectiveDateTo: formatDateInput(effectiveDateTo),
    expiryDuration,
    expiryDate: formatDateInput(expiryDate),
  };
};

const formatExpiryDuration = (duration) => {
  if (duration === "m") return "6 months";
  if (duration === "y") return "1 year";
  return duration || null;
};

const displayAddressValue = (...values) => {
  const value = values.find((item) => item != null && String(item).trim() !== "");
  return value ?? null;
};

const LEGACY_CORPORATE_DOCUMENT_BASE_URLS = {
  corporateLogo: "https://allaccess.net.ph/upload_doc/corporate_logo/",
  sobFile: "https://allaccess.net.ph/upload_doc/corporate_sob/",
  birFile: "https://allaccess.net.ph/upload_doc/corporate_bir/",
};

const corporateDocumentConfigs = [
  {
    key: "corporate_logo",
    label: "Corporate Logo",
    version2UrlKey: "corporate_logo_signed_url",
    version2PublicShareUrlKey: "corporate_logo_public_share_url",
    version2PublicShareTokenKey: "corporate_logo_public_share_token",
    version2PublicShareExpiresAtKey: "corporate_logo_public_share_expires_at",
    version2NameKey: "corporate_logo_file_name",
    version2UploadIdKey: "corporate_logo_upload_id",
    version2ExpiresAtKey: "corporate_logo_expires_at",
    legacyKey: "cobrand_logo_path",
    legacyBaseUrl: LEGACY_CORPORATE_DOCUMENT_BASE_URLS.corporateLogo,
    Icon: EyeIcon,
  },
  {
    key: "sob_file",
    label: "SOB File",
    version2UrlKey: "sob_file_signed_url",
    version2PublicShareUrlKey: "sob_file_public_share_url",
    version2PublicShareTokenKey: "sob_file_public_share_token",
    version2PublicShareExpiresAtKey: "sob_file_public_share_expires_at",
    version2NameKey: "sob_file_file_name",
    version2UploadIdKey: "sob_file_upload_id",
    version2ExpiresAtKey: "sob_file_expires_at",
    legacyKey: "sob_file",
    legacyBaseUrl: LEGACY_CORPORATE_DOCUMENT_BASE_URLS.sobFile,
    Icon: DocumentIcon,
  },
  {
    key: "electronic_signature",
    label: "Electronic Signature",
    version2UrlKey: "electronic_signature_signed_url",
    version2PublicShareUrlKey: "electronic_signature_public_share_url",
    version2PublicShareTokenKey: "electronic_signature_public_share_token",
    version2PublicShareExpiresAtKey: "electronic_signature_public_share_expires_at",
    version2NameKey: "electronic_signature_file_name",
    version2UploadIdKey: "electronic_signature_upload_id",
    version2ExpiresAtKey: "electronic_signature_expires_at",
    legacyKey: "electronic_signature_path",
    legacyBaseUrl: LEGACY_CORPORATE_DOCUMENT_BASE_URLS.birFile,
    Icon: EyeIcon,
  },
  {
    key: "secretary_cert",
    label: "Secretary Certificate",
    version2UrlKey: "secretary_cert_signed_url",
    version2PublicShareUrlKey: "secretary_cert_public_share_url",
    version2PublicShareTokenKey: "secretary_cert_public_share_token",
    version2PublicShareExpiresAtKey: "secretary_cert_public_share_expires_at",
    version2NameKey: "secretary_cert_file_name",
    version2UploadIdKey: "secretary_cert_upload_id",
    version2ExpiresAtKey: "secretary_cert_expires_at",
    legacyKey: "secretary_cert_path",
    legacyBaseUrl: LEGACY_CORPORATE_DOCUMENT_BASE_URLS.birFile,
    Icon: DocumentIcon,
  },
];

const buildLegacyDocumentUrl = (value, baseUrl) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  if (/^https?:\/\//i.test(normalized)) return normalized;
  return `${baseUrl}${encodeURI(normalized.replace(/^\/+/, ""))}`;
};

const getDocumentFileName = (value, fallback = "File") => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return fallback;

  const withoutQuery = normalized.split("?")[0];
  const fileName = withoutQuery.split(/[\\/]/).pop() || fallback;

  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
};

const memberStatusMap = {
  0: { name: "Inactive", color: "red" },
  1: { name: "Active", color: "green" },
  2: { name: "Terminated", color: "red" },
  3: { name: "Suspended", color: "amber" },
  4: { name: "Expired", color: "gray" },
  default: { name: "Unknown", color: "gray" },
};

const corporateStatusMap = {
  0: { name: "Inactive", color: "red" },
  1: { name: "Active/Reactivated", color: "green" },
  2: { name: "Terminated/Cancelled", color: "red" },
  3: { name: "Suspended", color: "amber" },
  4: { name: "Expired", color: "gray" },
  5: { name: "Extended", color: "blue" },
  default: { name: "Unknown", color: "gray" },
};

const memberTypeMap = {
  p: { label: "P - Principal", color: "blue" },
  d: { label: "D - Dependent", color: "amber" },
  b: { label: "B - Both", color: "violet" },
};

const formatMemberName = (member = {}) =>
  `${member.last_name || ""}, ${member.first_name || ""} ${
    member.middle_name || ""
  }`
    .replace(/\s+/g, " ")
    .replace(/^,\s*/, "")
    .trim();

const formatMemberType = (value) =>
  memberTypeMap[String(value ?? "").trim().toLowerCase()] || {
    label: value || "N/A",
    color: "gray",
  };

const formatGender = (value) => {
  const normalized = String(value ?? "").trim().toUpperCase();
  if (normalized === "M") return "Male";
  if (normalized === "F") return "Female";
  return value || "N/A";
};

const calculateAge = (birthDate) => {
  if (!birthDate) return "N/A";
  const parsed = dayjs(birthDate);
  if (!parsed.isValid()) return "N/A";
  return dayjs().diff(parsed, "year");
};

const normalizeMemberStatusFlag = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const isMemberActiveLike = (value) =>
  [1, 6, 7, 8].includes(normalizeMemberStatusFlag(value));

const memberActionMenu = {
  width: 288,
  height: 300,
  margin: 12,
  gap: 8,
};

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - memberActionMenu.width;
  left = Math.max(
    memberActionMenu.margin,
    Math.min(left, viewportWidth - memberActionMenu.width - memberActionMenu.margin)
  );

  let top = rect.bottom + memberActionMenu.gap;
  let placement = "bottom";

  if (top + memberActionMenu.height > viewportHeight - memberActionMenu.margin) {
    top = rect.top - memberActionMenu.height - memberActionMenu.gap;
    placement = "top";
  }

  const maxTop = Math.max(
    memberActionMenu.margin,
    viewportHeight - memberActionMenu.height - memberActionMenu.margin
  );
  top = Math.max(memberActionMenu.margin, Math.min(top, maxTop));

  return { top, left, placement };
};

const getMemberActionKey = (member) => member?.member_id || member?.tbl_id || "";

const HEADER_ACTION_BUTTON_BASE =
  "inline-flex h-8 items-center justify-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold shadow-sm transition-all duration-150 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-1";

const MEMBER_EXPORT_PAGE_SIZE = 100;

const memberExportColumns = [
  { key: "company", label: "Company" },
  { key: "employee_id", label: "Employee ID" },
  { key: "member_id", label: "Member ID" },
  { key: "member_name", label: "Member Name" },
  { key: "birthday", label: "Birthday" },
  { key: "gender", label: "Gender" },
  { key: "age", label: "Age" },
  { key: "member_type", label: "Member Type" },
  { key: "position", label: "Position" },
  { key: "plan", label: "Plan" },
  { key: "contact_number", label: "Contact Number" },
  { key: "email", label: "Email" },
  { key: "status", label: "Status" },
  { key: "renewal_date", label: "Renewal Date" },
  { key: "expiry_date", label: "Expiry Date" },
];

const idExtractionColumns = [
  { key: "last_name_and_dob", label: "LAST_NAME_AND_DOB" },
  { key: "last_name_first_name", label: "LAST_NAME_FIRST_NAME" },
  { key: "corp_code", label: "CORP_CODE" },
  { key: "corp_name", label: "corp_name" },
  { key: "employee_id", label: "employee_id" },
  { key: "member_id", label: "member_id" },
  { key: "last_name", label: "LAST_NAME" },
  { key: "first_name", label: "FIRST_NAME" },
  { key: "middle_name", label: "MIDDLE_NAME" },
  { key: "suffix", label: "SUFFIX" },
  { key: "date_of_birth", label: "date_of_birth" },
  { key: "status", label: "Status" },
];

const cardProductionColumns = [
  { key: "member_id", label: "Member ID" },
  { key: "employee_id", label: "employee_id" },
  { key: "full_name_1", label: "Full Name 1" },
  { key: "full_name_2", label: "Full Name 2" },
  { key: "first_name", label: "First Name" },
  { key: "middle_initial", label: "Middle Initial" },
  { key: "last_name", label: "Last Name" },
  { key: "suffix", label: "Suffix" },
  { key: "corporate_name", label: "Corporate Name" },
  { key: "card_name", label: "Card Name" },
  { key: "birthday", label: "Birthday" },
  { key: "gender", label: "gender" },
  { key: "expiry_date", label: "expiry_date" },
  { key: "member_type", label: "Member Type" },
  { key: "corp_status", label: "Corp Status" },
  { key: "member_status", label: "Member Status" },
];

const memberExportActions = [
  {
    type: "csv",
    label: "Export CSV",
    Icon: ArrowDownTrayIcon,
    className:
      "border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700 focus:ring-blue-500",
  },
  {
    type: "xlsx",
    label: "Export XLSX",
    Icon: TableCellsIcon,
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100 focus:ring-emerald-500",
  },
  {
    type: "pdf",
    label: "Export PDF",
    Icon: DocumentArrowDownIcon,
    className:
      "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100 focus:ring-rose-500",
  },
  {
    type: "id-extraction",
    label: "Export ID Extraction",
    Icon: IdentificationIcon,
    className:
      "border-cyan-200 bg-cyan-50 text-cyan-700 hover:border-cyan-300 hover:bg-cyan-100 focus:ring-cyan-500",
  },
  {
    type: "card-production",
    label: "Export Card Production",
    Icon: CreditCardIcon,
    className:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-100 focus:ring-indigo-500",
  },
];

const exportFileStamp = () => dayjs().format("YYYYMMDD_HHmmss");

const formatExportDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(value);
};

const formatDobKeyDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid()
    ? `${parsed.year()}-${parsed.month() + 1}-${parsed.date()}`
    : String(value);
};

const getStatusName = (value) => {
  const normalized = normalizeMemberStatusFlag(value);
  return memberStatusMap[normalized]?.name || value || "N/A";
};

const getCorporateStatus = (value) => {
  const normalized = normalizeMemberStatusFlag(value);
  if (corporateStatusMap[normalized]) {
    return corporateStatusMap[normalized];
  }

  const textValue = String(value ?? "").trim();
  const normalizedText = textValue.toLowerCase();
  if (["active", "active/reactivated"].includes(normalizedText)) {
    return corporateStatusMap[1];
  }
  if (normalizedText === "inactive") {
    return corporateStatusMap[0];
  }
  if (["terminated", "terminated/cancelled"].includes(normalizedText)) {
    return corporateStatusMap[2];
  }
  if (normalizedText === "suspended") {
    return corporateStatusMap[3];
  }
  if (normalizedText === "expired") {
    return corporateStatusMap[4];
  }
  if (normalizedText === "extended") {
    return corporateStatusMap[5];
  }

  return textValue
    ? { name: textValue, color: "gray" }
    : corporateStatusMap.default;
};

const getCorporateStatusName = (value) => {
  return getCorporateStatus(value).name || "N/A";
};

const getMiddleInitial = (value) => {
  const normalized = String(value || "").trim();
  return normalized ? `${normalized.charAt(0).toUpperCase()}.` : "";
};

const getCorporateDisplayName = (member, data) =>
  member?.actual_corp_name ||
  member?.corp_name ||
  data?.actual_corp_name ||
  data?.corp_name ||
  "";

const getCorporateCardName = (member, data) =>
  data?.corp_card_name ||
  member?.corp_card_name ||
  member?.actual_corp_name ||
  member?.corp_name ||
  data?.corp_name ||
  "";

const buildFullNameOne = (member) =>
  `${member?.last_name || ""}, ${member?.first_name || ""} ${getMiddleInitial(
    member?.middle_name
  )} ${member?.suffix || ""}`
    .replace(/\s+/g, " ")
    .replace(/^,\s*/, "")
    .trim();

const buildFullNameTwo = (member) =>
  `${member?.first_name || ""} ${getMiddleInitial(member?.middle_name)} ${
    member?.last_name || ""
  } ${member?.suffix || ""}`
    .replace(/\s+/g, " ")
    .trim();

const escapeCsvValue = (value) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const buildCsvContent = (columns, rows, trailingComma = false) => {
  const header = columns.map((column) => column.label).join(",");
  const body = rows.map((row) =>
    columns.map((column) => escapeCsvValue(row[column.key])).join(",")
  );
  const suffix = trailingComma ? "," : "";
  return [header + suffix, ...body.map((line) => line + suffix)].join("\n");
};

const downloadTextFile = (filename, content, mimeType = "text/csv;charset=utf-8;") => {
  const blob = new Blob([content], { type: mimeType });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const openPrintablePdf = (title, columns, rows) => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return false;

  const headerCells = columns
    .map((column) => `<th>${escapeHtml(column.label)}</th>`)
    .join("");
  const bodyRows = rows
    .map(
      (row) =>
        `<tr>${columns
          .map((column) => `<td>${escapeHtml(row[column.key])}</td>`)
          .join("")}</tr>`
    )
    .join("");

  printWindow.document.write(`<!doctype html>
<html>
<head>
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4 landscape; margin: 8mm; }
    body { font-family: Arial, sans-serif; color: #111827; margin: 0; }
    h1 { font-size: 15px; margin: 0 0 8px; }
    table { border-collapse: collapse; width: 100%; table-layout: fixed; }
    th, td { border: 1px solid #d1d5db; padding: 3px 4px; font-size: 7px; text-align: left; vertical-align: top; word-break: break-word; }
    th { background: #f3f4f6; font-weight: 700; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
  <script>
    window.onload = function () {
      window.focus();
      window.print();
    };
  </script>
</body>
</html>`);
  printWindow.document.close();
  return true;
};

const toMemberExportRows = (members, data) =>
  members.map((member) => {
    const memberType = formatMemberType(member.member_type);
    return {
      company: getCorporateDisplayName(member, data),
      employee_id: member.employee_id || "",
      member_id: member.member_id || "",
      member_name: formatMemberName(member),
      birthday: formatExportDate(member.date_of_birth),
      gender: formatGender(member.gender),
      age: calculateAge(member.date_of_birth),
      member_type: memberType.label,
      position: member.position || "",
      plan: member.plan_name || member.plan_code || "",
      contact_number: member.mob_tel || "",
      email: member.email_add || "",
      status: getStatusName(member.status_flag),
      renewal_date: formatExportDate(member.renewal_date),
      expiry_date: formatExportDate(member.expiry_date),
    };
  });

const toIdExtractionRows = (members, data) =>
  members.map((member) => ({
    last_name_and_dob: `${member.last_name || ""}_${formatDobKeyDate(
      member.date_of_birth
    )}`,
    last_name_first_name: `${member.last_name || ""}, ${member.first_name || ""}`
      .replace(/\s+/g, " ")
      .replace(/^,\s*/, "")
      .trim(),
    corp_code: `'${member.corp_code || data?.corp_code || ""}`,
    corp_name: getCorporateDisplayName(member, data),
    employee_id: member.employee_id || "",
    member_id: member.member_id || "",
    last_name: member.last_name || "",
    first_name: member.first_name || "",
    middle_name: member.middle_name || "",
    suffix: member.suffix || "",
    date_of_birth: formatExportDate(member.date_of_birth),
    status: getStatusName(member.status_flag),
  }));

const toCardProductionRows = (members, data) =>
  members.map((member) => ({
    member_id: member.member_id || "",
    employee_id: member.employee_id || "",
    full_name_1: buildFullNameOne(member),
    full_name_2: buildFullNameTwo(member),
    first_name: member.first_name || "",
    middle_initial: getMiddleInitial(member.middle_name),
    last_name: member.last_name || "",
    suffix: member.suffix || "",
    corporate_name: getCorporateDisplayName(member, data),
    card_name: getCorporateCardName(member, data),
    birthday: formatExportDate(member.date_of_birth),
    gender: member.gender || "",
    expiry_date: formatExportDate(member.expiry_date),
    member_type: member.member_type || "",
    corp_status: getCorporateStatusName(data?.status_flag),
    member_status: getStatusName(member.status_flag),
  }));

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    amber: "bg-amber-100 text-amber-700 ring-amber-200",
    violet: "bg-violet-100 text-violet-700 ring-violet-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = "blue" }) => {
  const accents = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="group rounded-xl border border-gray-100 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div className={clsx("rounded-lg p-2", accents[accent])}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-base font-semibold text-gray-900">
            {value ?? "—"}
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, copyable = false }) => {
  const [copied, setCopied] = useState(false);
  const canCopy = copyable && value;
  const onCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.info("Copy failed");
    }
  };
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="w-1/3 min-w-[160px] text-sm font-medium text-gray-600">
        {label}
      </div>
      <div className="flex-1 text-sm text-gray-900 break-words">
        {value ?? "—"}
        {canCopy && (
          <button
            type="button"
            onClick={onCopy}
            className="ml-2 inline-flex items-center gap-1 rounded-md border border-gray-200 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
            title="Copy to clipboard"
          >
            {copied ? (
              <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
            ) : (
              <ClipboardIcon className="h-5 w-5" />
            )}
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, label, Icon }) => (
  <button
    onClick={onClick}
    className={clsx(
      "relative inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
      active ? "text-blue-700" : "text-gray-600 hover:text-gray-800"
    )}
  >
    {Icon ? <Icon className="h-5 w-5" /> : null}
    <span>{label}</span>
    {active && (
      <span className="absolute inset-x-2 -bottom-1 h-0.5 rounded-full bg-blue-600" />
    )}
  </button>
);

const SkeletonBlock = () => (
  <div className="animate-pulse">
    <div className="mb-4 h-6 w-1/3 rounded bg-gray-200" />
    <div className="space-y-2">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-4 w-full rounded bg-gray-100" />
      ))}
    </div>
  </div>
);

const EmptyState = ({
  title = "No data",
  subtitle = "Nothing to show here yet.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <InboxIcon className="mb-2 h-8 w-8 text-gray-400" />
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const CorporateViewModal = ({ corpCode, onClose }) => {
  const navigate = useNavigate();
  const { corpCode: routeCorpCode } = useParams();
  const resolvedCorpCode = corpCode || routeCorpCode;
  const isFullPage = !onClose;
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [activeTab, setActiveTab] = useState("information");
  const [isMaximized, setIsMaximized] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // FOR FUND DATA
  // Add these states
  const [fundData, setFundData] = useState(null); // Separate state for fund data
  const [fundLoading, setFundLoading] = useState(false); // For loading indicator in Fund tab
  const [memberRows, setMemberRows] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberTotalCount, setMemberTotalCount] = useState(0);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberCurrentPage, setMemberCurrentPage] = useState(1);
  const [memberItemsPerPage, setMemberItemsPerPage] = useState(10);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [isMemberViewOpen, setIsMemberViewOpen] = useState(false);
  const [openMemberActionMenu, setOpenMemberActionMenu] = useState(null);
  const [memberActionMenuPosition, setMemberActionMenuPosition] = useState(null);
  const [memberExporting, setMemberExporting] = useState(null);
  const [regeneratingDocumentKey, setRegeneratingDocumentKey] = useState(null);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    navigate("/corporate");
  }, [navigate, onClose]);

  const handleViewFullPage = useCallback(() => {
    if (!resolvedCorpCode) return;
    navigate(`/corporate/view/${encodeURIComponent(resolvedCorpCode)}`);
  }, [navigate, resolvedCorpCode]);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  }), []);

  const handleRegenerateVersion2Url = useCallback(
    async (documentConfig) => {
      const uploadId = formData?.[documentConfig.version2UploadIdKey];
      if (!uploadId) {
        toast.info("No Version 2.0 upload ID available.");
        return;
      }

      setRegeneratingDocumentKey(documentConfig.key);
      try {
        const response = await axios.post(
          `${BASE_URL}/corporates/document/regenerate-url`,
          { upload_id: uploadId },
          { headers: getAuthHeaders() }
        );
        const {
          signed_url,
          url_expires_at,
          public_share_token,
          public_share_url,
          public_share_expires_at,
        } = response.data;
        const version2Url = public_share_url || signed_url;

        setFormData((prev) => ({
          ...prev,
          [documentConfig.version2UrlKey]: version2Url,
          [documentConfig.version2ExpiresAtKey]: url_expires_at,
          [documentConfig.version2PublicShareUrlKey]: public_share_url,
          [documentConfig.version2PublicShareTokenKey]: public_share_token,
          [documentConfig.version2PublicShareExpiresAtKey]:
            public_share_expires_at,
        }));
        toast.success(`${documentConfig.label} Version 2.0 URL regenerated.`);
      } catch (error) {
        toast.error(
          "Failed to regenerate URL: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        setRegeneratingDocumentKey(null);
      }
    },
    [formData, getAuthHeaders]
  );

  useEffect(() => {
    const fetchCorporate = async () => {
      if (!resolvedCorpCode) return;
      setIsLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/corporates/${resolvedCorpCode}`, {
          // Change to non-v2 for faster load
          headers: getAuthHeaders(),
        });
        setFormData(response.data.data);
      } catch (error) {
        toast.error(
          "Failed to fetch corporate details: " +
            (error.response?.data?.message || error.message)
        );
        handleClose();
      } finally {
        setIsLoading(false);
      }
    };
    fetchCorporate();
  }, [resolvedCorpCode, getAuthHeaders, handleClose]);

  // New useEffect for lazy loading fund data on tab change
  useEffect(() => {
    if (activeTab === "fund" && !fundData && resolvedCorpCode) {
      const fetchFundData = async () => {
        setFundLoading(true);
        try {
          const response = await axios.get(
            `${BASE_URL}/corporates/${resolvedCorpCode}/funds`,
            {
              headers: getAuthHeaders(),
            }
          );
          setFundData(response.data.data);
          // Merge into formData for compatibility with existing JSX
          setFormData((prev) => ({ ...prev, ...response.data.data }));
        } catch (error) {
          toast.error(
            "Failed to fetch fund details: " +
              (error.response?.data?.message || error.message)
          );
        } finally {
          setFundLoading(false);
        }
      };
      fetchFundData();
    }
  }, [activeTab, resolvedCorpCode, fundData, getAuthHeaders]);
  // FOR FUND DATA

  const fetchCorporateMembers = useCallback(async () => {
    if (!resolvedCorpCode) return;

    setMemberLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/members`, {
        headers: getAuthHeaders(),
        params: {
          limit_offset: (memberCurrentPage - 1) * memberItemsPerPage,
          search_string: memberSearchQuery,
          sort_by: "member_id",
          sort_direction: "asc",
          limit: memberItemsPerPage,
          corp_code: resolvedCorpCode,
        },
      });

      setMemberRows(response.data.members || []);
      setMemberTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error(
        "Failed to fetch corporate members: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setMemberLoading(false);
    }
  }, [
    resolvedCorpCode,
    getAuthHeaders,
    memberCurrentPage,
    memberItemsPerPage,
    memberSearchQuery,
  ]);

  const fetchCorporateMembersForExport = useCallback(async () => {
    if (!resolvedCorpCode) return [];

    let offset = 0;
    let totalCount = null;
    const rows = [];

    do {
      const response = await axios.get(`${BASE_URL}/members`, {
        headers: getAuthHeaders(),
        params: {
          limit_offset: offset,
          search_string: memberSearchQuery,
          sort_by: "member_id",
          sort_direction: "asc",
          limit: MEMBER_EXPORT_PAGE_SIZE,
          corp_code: resolvedCorpCode,
        },
      });

      const batch = response.data.members || [];
      rows.push(...batch);
      totalCount = Number(response.data.total_count || rows.length);
      offset += MEMBER_EXPORT_PAGE_SIZE;

      if (batch.length === 0) break;
    } while (rows.length < totalCount);

    return rows;
  }, [getAuthHeaders, memberSearchQuery, resolvedCorpCode]);

  const handleMemberExport = useCallback(
    async (exportType) => {
      if (memberExporting) return;

      setMemberExporting(exportType);
      try {
        const rows = await fetchCorporateMembersForExport();
        if (rows.length === 0) {
          toast.info("No members available to export.");
          return;
        }

        const corpCodeForFile = String(
          formData?.corp_code || resolvedCorpCode || "corporate"
        ).replace(/[^a-zA-Z0-9_-]/g, "");
        const stamp = exportFileStamp();

        if (exportType === "csv") {
          const exportRows = toMemberExportRows(rows, formData);
          downloadTextFile(
            `corporate_members_${corpCodeForFile}_${stamp}.csv`,
            buildCsvContent(memberExportColumns, exportRows)
          );
          toast.success("Members CSV exported.");
          return;
        }

        if (exportType === "xlsx") {
          const exportRows = toMemberExportRows(rows, formData);
          const XLSX = await import("xlsx");
          const worksheet = XLSX.utils.json_to_sheet(
            exportRows.map((row) =>
              memberExportColumns.reduce((acc, column) => {
                acc[column.label] = row[column.key] ?? "";
                return acc;
              }, {})
            )
          );
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Members");
          XLSX.writeFile(
            workbook,
            `corporate_members_${corpCodeForFile}_${stamp}.xlsx`
          );
          toast.success("Members XLSX exported.");
          return;
        }

        if (exportType === "pdf") {
          const exportRows = toMemberExportRows(rows, formData);
          const opened = openPrintablePdf(
            `Corporate Members - ${formData?.corp_name || corpCodeForFile}`,
            memberExportColumns,
            exportRows
          );
          if (!opened) {
            toast.error("Please allow pop-ups to export PDF.");
            return;
          }
          toast.success("PDF export opened in landscape print view.");
          return;
        }

        if (exportType === "id-extraction") {
          const exportRows = toIdExtractionRows(rows, formData);
          downloadTextFile(
            `id_extraction_${corpCodeForFile}_${stamp}.csv`,
            buildCsvContent(idExtractionColumns, exportRows, true)
          );
          toast.success("ID Extraction CSV exported.");
          return;
        }

        if (exportType === "card-production") {
          const exportRows = toCardProductionRows(rows, formData);
          downloadTextFile(
            `card_production_${corpCodeForFile}_${stamp}.csv`,
            buildCsvContent(cardProductionColumns, exportRows, true)
          );
          toast.success("Card Production CSV exported.");
        }
      } catch (error) {
        toast.error(
          "Failed to export members: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        setMemberExporting(null);
      }
    },
    [
      fetchCorporateMembersForExport,
      formData,
      memberExporting,
      resolvedCorpCode,
    ]
  );

  useEffect(() => {
    if (activeTab === "members") {
      fetchCorporateMembers();
    }
  }, [activeTab, fetchCorporateMembers]);

  useEffect(() => {
    setMemberCurrentPage(1);
  }, [memberSearchQuery, memberItemsPerPage]);

  const closeMemberActionMenu = useCallback(() => {
    setOpenMemberActionMenu(null);
    setMemberActionMenuPosition(null);
  }, []);

  const handleMemberActionMenuToggle = useCallback(
    (member, event) => {
      event.stopPropagation();
      const rowKey = getMemberActionKey(member);

      if (openMemberActionMenu === rowKey) {
        closeMemberActionMenu();
        return;
      }

      setOpenMemberActionMenu(rowKey);
      setMemberActionMenuPosition(getActionMenuPosition(event.currentTarget));
    },
    [closeMemberActionMenu, openMemberActionMenu]
  );

  useEffect(() => {
    if (!openMemberActionMenu) {
      return undefined;
    }

    const handleViewportChange = () => closeMemberActionMenu();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeMemberActionMenu, openMemberActionMenu]);

  const handleMemberStatusToggle = useCallback(
    async (member) => {
      const currentStatus = normalizeMemberStatusFlag(member?.status_flag);
      const shouldDeactivate = isMemberActiveLike(currentStatus);
      const targetStatusFlag = shouldDeactivate ? 0 : 1;
      const actionLabel = shouldDeactivate ? "Deactivate" : "Activate";
      const actionLabelLower = actionLabel.toLowerCase();
      const memberType = String(member?.member_type || "").trim().toLowerCase();
      const isPrincipalMember = memberType === "p";
      const memberDisplayName = formatMemberName(member);

      const result = await Swal.fire({
        title: `${actionLabel} Member`,
        html: `
          <div class="text-left">
            <p><strong>Member ID:</strong> ${member?.member_id || "N/A"}</p>
            <p><strong>Name:</strong> ${memberDisplayName || "N/A"}</p>
            <p><strong>Current Status:</strong> ${
              memberStatusMap[currentStatus]?.name || member?.status_flag || "Unknown"
            }</p>
            <p><strong>Target Status:</strong> ${
              targetStatusFlag === 1 ? "Active" : "Inactive"
            }</p>
            ${
              isPrincipalMember
                ? `
              <div class="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                <label class="flex items-center gap-2 text-sm text-gray-700">
                  <input id="apply_to_dependents_checkbox" type="checkbox" class="h-4 w-4" />
                  Apply this action to all dependents of this principal
                </label>
              </div>
            `
                : ""
            }
          </div>
          <p>Are you sure you want to ${actionLabelLower} this member?</p>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          cancelButton:
            "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
        },
        preConfirm: () => {
          const dependentCheckbox = document.getElementById(
            "apply_to_dependents_checkbox"
          );
          return {
            applyToDependents: dependentCheckbox
              ? Boolean(dependentCheckbox.checked)
              : false,
          };
        },
      });

      if (!result.isConfirmed) return;

      const applyToDependents =
        isPrincipalMember && Boolean(result?.value?.applyToDependents);

      try {
        const response = await axios.post(
          `${BASE_URL}/members/${member.member_id}/status`,
          {
            type: "activate",
            status_flag: targetStatusFlag,
            apply_to_dependents: applyToDependents ? 1 : 0,
            bank_id: "",
            bank_branch: "",
          },
          { headers: getAuthHeaders() }
        );

        if (response?.data?.status === "success") {
          const backendMessage = response.data.message;
          const successMessage = Array.isArray(backendMessage)
            ? backendMessage[0]
            : backendMessage || `Member ${actionLabelLower}d successfully.`;
          toast.success(successMessage);
          fetchCorporateMembers();
          return;
        }

        const backendMessage = response?.data?.message;
        toast.error(
          (Array.isArray(backendMessage) ? backendMessage[0] : backendMessage) ||
            `Failed to ${actionLabelLower} member.`
        );
      } catch (error) {
        const backendMessage = error?.response?.data?.message;
        toast.error(
          (Array.isArray(backendMessage) ? backendMessage[0] : backendMessage) ||
            error.message ||
            `Failed to ${actionLabelLower} member.`
        );
      }
    },
    [fetchCorporateMembers, getAuthHeaders]
  );

  const handleResendWelcomeAlert = useCallback(
    async (member) => {
      const result = await Swal.fire({
        title: "Resend Member's Text Welcome Alert",
        html: `
          <div class="text-left">
            <p><strong>Member:</strong> ${formatMemberName(member) || "N/A"}</p>
            <p><strong>Mobile#:</strong> ${member?.mob_tel || "N/A"}</p>
            <p><strong>Email#:</strong> ${member?.email_add || "N/A"}</p>
          </div>
          <p>Are you sure you want to resend welcome text alert?</p>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          cancelButton:
            "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
        },
      });

      if (!result.isConfirmed) return;

      const modeResult = await Swal.fire({
        title: "Resend Welcome Alert via",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        showDenyButton: true,
        confirmButtonText: "Email",
        denyButtonText: "SMS",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          denyButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          cancelButton:
            "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
        },
      });

      if (!modeResult.isConfirmed && !modeResult.isDenied) return;

      const mode = modeResult.isConfirmed ? "email" : "sms";
      try {
        const response = await axios.post(
          `${BASE_URL}/members/resend-welcome-alert/${member.member_id}`,
          { mode },
          { headers: getAuthHeaders() }
        );

        if (response.data.status === "success") {
          toast.success(response.data.message);
        } else {
          toast.error(response.data.message || `Failed to send ${mode} alert.`);
        }
      } catch (error) {
        toast.error(
          `Failed to send ${mode} alert: ` +
            (error.response?.data?.message || error.message)
        );
      }
    },
    [getAuthHeaders]
  );

  useEffect(() => {
    const fetchCorporate = async () => {
      if (!resolvedCorpCode) return;
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/corporates/v2/${resolvedCorpCode}`,
          {
            headers: getAuthHeaders(),
          }
        );
        setFormData(response.data.data);
      } catch (error) {
        toast.error(
          "Failed to fetch corporate details: " +
            (error.response?.data?.message || error.message)
        );
        handleClose();
      } finally {
        setIsLoading(false);
      }
    };
    fetchCorporate();
  }, [resolvedCorpCode, getAuthHeaders, handleClose]);

  useEffect(() => {
    if (isFullPage) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [handleClose, isFullPage]);

  const toggleMaximize = () => setIsMaximized((s) => !s);

  const filteredFundContacts = useMemo(() => {
    const list = formData?.fund_contacts || [];
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (c) =>
        c.fund_name?.toLowerCase().includes(q) ||
        c.fund_mobile?.toLowerCase().includes(q) ||
        c.fund_email?.toLowerCase().includes(q)
    );
  }, [formData, searchQuery]);

  const totalItems = filteredFundContacts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const paginatedFundContacts = filteredFundContacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const policyPeriod = useMemo(() => getPolicyPeriod(formData), [formData]);
  const memberTotalPages =
    Math.ceil(memberTotalCount / memberItemsPerPage) || 1;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };
  const handleMemberPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= memberTotalPages) {
      setMemberCurrentPage(newPage);
    }
  };
  const handleMemberItemsPerPageChange = (e) => {
    setMemberItemsPerPage(Number(e.target.value));
    setMemberCurrentPage(1);
  };

  const getMemberRowActions = (member) => [
    {
      key: "view",
      label: "View details",
      Icon: EyeIcon,
      color: "text-sky-600",
      onClick: () => {
        setSelectedMemberId(member.member_id);
        setIsMemberViewOpen(true);
      },
    },
    {
      key: "edit",
      label: "Edit member",
      Icon: PencilSquareIcon,
      color: "text-blue-600",
      onClick: () => {
        if (onClose) onClose();
        navigate(`/members/edit/${member.member_id}`);
      },
    },
    {
      key: "history",
      label: "Movement history",
      Icon: ClockIcon,
      color: "text-violet-600",
      onClick: () => {
        if (onClose) onClose();
        navigate(
          `/members?member_id=${encodeURIComponent(
            member.member_id
          )}&open_history=1`
        );
      },
    },
    {
      key: "status",
      label: isMemberActiveLike(member.status_flag)
        ? "Deactivate member"
        : "Activate member",
      Icon: DocumentCheckIcon,
      color: "text-amber-600",
      onClick: () => handleMemberStatusToggle(member),
    },
    {
      key: "resend",
      label: "Resend welcome alert",
      Icon: RocketLaunchIcon,
      color: "text-rose-600",
      onClick: () => handleResendWelcomeAlert(member),
    },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const tabs = [
    { key: "information", label: "Information", icon: ShieldCheckIcon },
    { key: "address", label: "Address", icon: MapPinIcon },
    { key: "documents", label: "Documents", icon: DocumentIcon },
    { key: "policy", label: "Policy", icon: ClockIcon },
    { key: "billing", label: "Billing", icon: CurrencyDollarIcon },
    { key: "fund", label: "Fund", icon: CurrencyDollarIcon },
    { key: "members", label: "Members", icon: UserGroupIcon },
  ];

  if (isLoading || !formData) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={clsx(
          "flex items-center justify-center",
          isFullPage
            ? "min-h-screen bg-slate-50 p-4"
            : "fixed inset-0 z-50 bg-black/50"
        )}
      >
        <div className="w-full max-w-5xl rounded-2xl bg-white p-8 shadow-xl">
          <div className="mb-6 flex items-center justify-center gap-2">
            <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-600" />
            <span className="text-sm text-gray-600">
              Loading corporate details…
            </span>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <SkeletonBlock />
            <SkeletonBlock />
          </div>
        </div>
      </motion.div>
    );
  }

  const corporateStatus = getCorporateStatus(formData.status_flag);
  const statusColor = corporateStatus.color;
  const industryDisplay =
    formData.corp_industry_desc || formData.corp_industry_code || "—";
  const corporateTypeDisplay =
    formData.corp_type_desc || formData.corp_type_code || "—";

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={clsx(
          "flex items-center justify-center",
          isFullPage
            ? "min-h-screen bg-slate-50 p-4"
            : "fixed inset-0 z-50 bg-black/50",
          !isFullPage && (isMaximized ? "p-0" : "p-4")
        )}
        onClick={isFullPage ? undefined : handleClose}
      >
        <motion.div
          layout
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className={clsx(
            "relative flex min-w-0 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl",
            isFullPage
              ? "min-h-[calc(100vh-2rem)] w-full max-w-[96rem]"
              : isMaximized
              ? "h-[100vh] w-full max-w-none"
              : "h-[92vh] w-[calc(100vw-2rem)] max-w-6xl"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative shrink-0">
            <div className="h-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
            <div className="absolute inset-x-0 top-0 flex items-center justify-between px-4 py-4">
              <div className="flex items-center gap-3 text-white">
                <div className="rounded-lg bg-white/10 p-2 ring-1 ring-white/20 backdrop-blur">
                  <ShieldCheckIcon className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">
                      {formData.corp_name || "Corporate"}
                    </h2>
                    <Badge color="violet">{formData.corp_code}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/90">
                    <span>{formData.actual_corp_name || "—"}</span>
                    <span className="opacity-50">•</span>
                    <Badge color={statusColor}>
                      {corporateStatus.name}
                    </Badge>
                    <span className="opacity-50">•</span>
                    <Badge color="blue">{industryDisplay}</Badge>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isFullPage && (
                  <button
                    type="button"
                    onClick={handleViewFullPage}
                    className="relative inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white ring-1 ring-white/20 hover:bg-white/20"
                    title="View in Full Page"
                  >
                    <span>View in Full Page</span>
                    <span className="absolute -right-2 -top-2 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white ring-1 ring-white">
                      New
                    </span>
                  </button>
                )}
                {!isFullPage && (
                  <button
                    onClick={toggleMaximize}
                    className="rounded-lg bg-white/10 p-2 text-white ring-1 ring-white/20 hover:bg-white/20"
                    title={isMaximized ? "Restore" : "Maximize"}
                  >
                    {isMaximized ? (
                      <ArrowsPointingInIcon className="h-5 w-5" />
                    ) : (
                      <ArrowsPointingOutIcon className="h-5 w-5" />
                    )}
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="rounded-lg bg-white/10 p-2 text-white ring-1 ring-white/20 hover:bg-white/20"
                  title={isFullPage ? "Back to Corporate" : "Close"}
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="shrink-0 border-b border-gray-100 bg-white/80 backdrop-blur">
            <div className="no-scrollbar flex gap-1 overflow-x-auto px-4">
              {tabs.map((t) => (
                <TabButton
                  key={t.key}
                  active={activeTab === t.key}
                  onClick={() => setActiveTab(t.key)}
                  label={t.label}
                  Icon={t.icon}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {/* Overview cards */}
            {activeTab === "information" && (
              <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatCard
                  icon={ShieldCheckIcon}
                  label="Type"
                  value={corporateTypeDisplay}
                  accent="violet"
                />
                <StatCard
                  icon={CurrencyDollarIcon}
                  label="Admin Fee"
                  value={formData.admin_fee ?? "—"}
                  accent="amber"
                />
                <StatCard
                  icon={ClockIcon}
                  label="Effective"
                  value={formatDate(formData.effective_date)}
                  accent="blue"
                />
                <StatCard
                  icon={UserGroupIcon}
                  label="Industry"
                  value={industryDisplay}
                  accent="emerald"
                />
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {activeTab === "information" && (
                <>
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Basic Information
                    </h3>
                    <div className="divide-y divide-gray-100">
                      <InfoRow
                        label="Corporate Code"
                        value={formData.corp_code}
                        copyable
                      />
                      <InfoRow
                        label="Corporate Prefix"
                        value={formData.corp_prefix}
                      />
                      <InfoRow
                        label="Corporate Name"
                        value={formData.corp_name}
                      />
                      <InfoRow
                        label="Actual Corporate Name"
                        value={formData.actual_corp_name}
                      />
                      <InfoRow
                        label="Corporate Card Name"
                        value={formData.corp_card_name}
                      />
                      <InfoRow
                        label="Licensed Name"
                        value={formData.licensed_name}
                      />
                      <InfoRow
                        label="Industry"
                        value={`${industryDisplay}${
                          formData.corp_industry_others
                            ? ` (${formData.corp_industry_others})`
                            : ""
                        }`}
                      />
                      <InfoRow
                        label="Corporate Type"
                        value={corporateTypeDisplay}
                      />
                      <InfoRow label="PHIC No" value={formData.phic_no} />
                      <InfoRow label="Min Age" value={formData.min_age} />
                      <InfoRow label="Max Age" value={formData.max_age} />
                      <InfoRow label="Remarks" value={formData.remarks} />
                      <InfoRow
                        label="Co-brand Layer"
                        value={
                          formData.cobrand_layer === "1"
                            ? "1st Layer"
                            : formData.cobrand_layer
                        }
                      />
                      <InfoRow
                        label="Co-brand Tag"
                        value={formData.cobrand_tag ? "Yes" : "No"}
                      />
                      <InfoRow
                        label="Special Expense"
                        value={formData.spec_exp ? "Yes" : "No"}
                      />
                      <InfoRow label="Main/Sub" value={formData.main_sub} />
                      <InfoRow
                        label="Main Corporate"
                        value={formData.main_corp}
                      />
                      <InfoRow label="Status" value={corporateStatus.name} />
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Contact Information
                    </h3>
                    <div className="divide-y divide-gray-100">
                      <InfoRow label="CEO Name" value={formData.ceo_name} />
                      <InfoRow label="COO Name" value={formData.coo_name} />
                      <InfoRow label="Care Of" value={formData.corp_careof} />
                      <InfoRow
                        label="Designation"
                        value={formData.corp_careof_designation}
                      />
                      <InfoRow label="Tel No" value={formData.tel_no} />
                      <InfoRow label="Fax No" value={formData.fax_no} />
                      <InfoRow label="Mobile Tel" value={formData.mobtel} />
                      <InfoRow
                        label="Email Address"
                        value={formData.email_add}
                        copyable
                      />
                      <InfoRow
                        label="Email Recipient"
                        value={formData.email_recipient}
                      />
                    </div>
                  </div>
                </>
              )}

              {activeTab === "address" && (
                <>
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Corporate Address
                    </h3>
                    <div className="divide-y divide-gray-100">
                      <InfoRow
                        label="Street No/Bldg"
                        value={formData.corp_streetno_bldg}
                      />
                      <InfoRow
                        label="Region"
                        value={displayAddressValue(
                          formData.resolved_corp_region,
                          formData.corp_region
                        )}
                      />
                      <InfoRow
                        label="Province"
                        value={displayAddressValue(
                          formData.resolved_corp_province,
                          formData.corp_province
                        )}
                      />
                      <InfoRow
                        label="Town/City"
                        value={displayAddressValue(
                          formData.resolved_corp_towncity,
                          formData.corp_towncity
                        )}
                      />
                      <InfoRow
                        label="Barangay"
                        value={displayAddressValue(
                          formData.resolved_corp_barangay,
                          formData.corp_barangay
                        )}
                      />
                      <InfoRow
                        label="Postal Code"
                        value={formData.corp_postalcode}
                      />
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Billing Address
                    </h3>
                    <div className="divide-y divide-gray-100">
                      <InfoRow
                        label="Street No/Bldg"
                        value={formData.bill_streetno_bldg}
                      />
                      <InfoRow
                        label="Region"
                        value={displayAddressValue(
                          formData.resolved_bill_region,
                          formData.bill_region
                        )}
                      />
                      <InfoRow
                        label="Province"
                        value={displayAddressValue(
                          formData.resolved_bill_province,
                          formData.bill_province
                        )}
                      />
                      <InfoRow
                        label="Town/City"
                        value={displayAddressValue(
                          formData.resolved_bill_towncity,
                          formData.bill_towncity
                        )}
                      />
                      <InfoRow
                        label="Barangay"
                        value={displayAddressValue(
                          formData.resolved_bill_barangay,
                          formData.bill_barangay
                        )}
                      />
                      <InfoRow
                        label="Postal Code"
                        value={formData.bill_postalcode}
                      />
                    </div>
                  </div>
                </>
              )}

              {activeTab === "documents" && (
                <div className="col-span-1 md:col-span-2">
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Documents
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {corporateDocumentConfigs.map((documentConfig) => {
                        const DocumentLinkIcon = documentConfig.Icon;
                        const version2PublicShareUrl =
                          formData[documentConfig.version2PublicShareUrlKey];
                        const version2Url =
                          version2PublicShareUrl ||
                          formData[documentConfig.version2UrlKey];
                        const version2Name = getDocumentFileName(
                          formData[documentConfig.version2NameKey]
                        );
                        const version2UploadId =
                          formData[documentConfig.version2UploadIdKey];
                        const version2ExpiresAt =
                          formData[
                            documentConfig.version2PublicShareExpiresAtKey
                          ] || formData[documentConfig.version2ExpiresAtKey];
                        const isRegenerating =
                          regeneratingDocumentKey === documentConfig.key;
                        const legacyValue = formData[documentConfig.legacyKey];
                        const legacyUrl = buildLegacyDocumentUrl(
                          legacyValue,
                          documentConfig.legacyBaseUrl
                        );
                        const legacyName = getDocumentFileName(legacyValue);

                        return (
                          <div
                            key={documentConfig.key}
                            className="rounded-lg border border-gray-100 p-4"
                          >
                            <div className="text-xs font-medium text-gray-500">
                              {documentConfig.label}
                            </div>
                            <div className="mt-3 space-y-2">
                              {version2Url ? (
                                <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
                                  <a
                                    href={version2Url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-2 text-sm text-blue-700 hover:underline"
                                  >
                                    <DocumentLinkIcon className="mt-0.5 h-5 w-5 shrink-0" />
                                    <span className="min-w-0">
                                      <span className="block font-semibold">
                                        Version 2.0
                                      </span>
                                      <span className="block break-words text-xs text-blue-700/80">
                                        {version2Name}
                                      </span>
                                      {version2ExpiresAt && (
                                        <span className="block text-xs text-blue-700/70">
                                          Expires:{" "}
                                          {formatDate(version2ExpiresAt, true)}
                                        </span>
                                      )}
                                    </span>
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRegenerateVersion2Url(
                                        documentConfig
                                      )
                                    }
                                    disabled={!version2UploadId || isRegenerating}
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    <ArrowPathIcon
                                      className={clsx(
                                        "h-4 w-4",
                                        isRegenerating && "animate-spin"
                                      )}
                                    />
                                    {isRegenerating
                                      ? "Regenerating..."
                                      : "Regenerate URL"}
                                  </button>
                                </div>
                              ) : (
                                <div className="rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm text-gray-500">
                                  No Version 2.0 file
                                </div>
                              )}

                              {legacyUrl ? (
                                <a
                                  href={legacyUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                                >
                                  <DocumentLinkIcon className="mt-0.5 h-5 w-5 shrink-0" />
                                  <span className="min-w-0">
                                    <span className="block font-semibold">
                                      Version 1.0
                                    </span>
                                    <span className="block break-words text-xs text-slate-500">
                                      {legacyName}
                                    </span>
                                  </span>
                                </a>
                              ) : (
                                <div className="rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm text-gray-500">
                                  No Version 1.0 file
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "policy" && (
                <div className="col-span-1 md:col-span-2">
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Policy Information
                    </h3>
                    <div className="divide-y divide-gray-100">
                      <InfoRow
                        label="Effective Date (from) *"
                        value={formatDate(policyPeriod.effectiveDateFrom)}
                      />
                      <InfoRow
                        label="Effective Date (to)"
                        value={formatDate(policyPeriod.effectiveDateTo)}
                      />
                      <InfoRow
                        label="Expiry Duration"
                        value={formatExpiryDuration(policyPeriod.expiryDuration)}
                      />
                      <InfoRow
                        label="Expiry Date *"
                        value={formatDate(policyPeriod.expiryDate)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "billing" && (
                <>
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Billing Information
                    </h3>
                    <div className="divide-y divide-gray-100">
                      <InfoRow
                        label="Billing Due Date"
                        value={formData.bill_duedate}
                      />
                      <InfoRow
                        label="Auto Deduct Tag"
                        value={formData.auto_deduct_tag ? "Yes" : "No"}
                      />
                      <InfoRow
                        label="Bank Account Name"
                        value={formData.bank_acc_name}
                      />
                      <InfoRow label="Bank No" value={formData.bank_no} />
                      <InfoRow label="Bank Name" value={formData.bank_name} />
                      <InfoRow
                        label="Bank Branch"
                        value={formData.bank_branch}
                      />
                      <InfoRow label="TIN" value={formData.tin} />
                      <InfoRow
                        label="Withheld"
                        value={formData.withheld ? "Yes" : "No"}
                      />
                      <InfoRow
                        label="Representative TIN"
                        value={formData.rep_tin}
                      />
                      <InfoRow
                        label="Representative Name"
                        value={formData.rep_name}
                      />
                      <InfoRow
                        label="Representative Position"
                        value={formData.rep_position}
                      />
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Payment Setup
                    </h3>
                    <div className="divide-y divide-gray-100">
                      <InfoRow
                        label="Payment Setup"
                        value={
                          formData.payment_setup === "0"
                            ? "Select"
                            : formData.payment_setup === "1"
                            ? "Blanket"
                            : "Non-Blanket"
                        }
                      />
                      <InfoRow label="Admin Fee" value={formData.admin_fee} />
                    </div>
                  </div>
                </>
              )}

              {activeTab === "fund" && (
                <div className="col-span-1 md:col-span-2">
                  {fundLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-600" />
                      <span className="ml-2 text-sm text-gray-600">
                        Loading fund details…
                      </span>
                    </div>
                  ) : (
                    // Existing Fund content here (it will use the merged formData)
                    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-base font-semibold text-gray-900">
                          Fund Information
                        </h3>
                        <Badge
                          color={formData.fund_mgmt_flag ? "green" : "gray"}
                        >
                          Fund Mgmt:{" "}
                          {formData.fund_mgmt_flag ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>

                      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="Revolving Fund"
                          value={formatCurrency(
                            formData.revolving_fund ??
                              formData._revolving_fund_amt ??
                              formData.revolving_fund_amt
                          )}
                        />
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="Total Added Fund"
                          value={formatCurrency(
                            formData.total_added_fund ?? formData._corp_fund
                          )}
                        />
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="Group Utilization"
                          value={formatCurrency(
                            formData.group_utilization ?? formData.total_util
                          )}
                        />
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="Group Reimbursement"
                          value={formatCurrency(
                            formData.group_reimbursement ??
                              formData.group_reim_util
                          )}
                        />
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="Account Utilization"
                          value={formatCurrency(
                            formData.account_utilization ?? formData.acct_util
                          )}
                        />
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="Account Reimbursement"
                          value={formatCurrency(
                            formData.account_reimbursement ??
                              formData.acct_reim_util
                          )}
                        />
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="Threshold"
                          value={formatCurrency(
                            formData.threshold ?? formData._threshold_amt
                              ?? formData.threshold_amt
                          )}
                        />
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="Remaining Fund"
                          value={formatCurrency(
                            formData.remaining_fund ??
                              formData.remaining_fund_amt
                          )}
                        />
                        <StatCard
                          icon={ClockIcon}
                          label="Util %"
                          value={formatFundUtilPercent(
                            formData.revolving_fund ??
                              formData._revolving_fund_amt ??
                              formData.revolving_fund_amt,
                            formData.remaining_fund ??
                              formData.remaining_fund_amt
                          )}
                        />
                        <StatCard
                          icon={CurrencyDollarIcon}
                          label="1st Warning Amount"
                          value={formatCurrency(
                            formData.first_warning_amount ??
                              formData._first_warning_amount
                          )}
                        />
                      </div>

                      <div className="mb-2 text-sm font-semibold text-gray-900">
                        Fund Contacts
                      </div>

                      <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="relative w-full md:max-w-sm">
                          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Search by name, mobile, or email"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Show</span>
                          <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <span className="text-xs text-gray-500">entries</span>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-lg border border-gray-200">
                        <div className="max-h-[40vh] overflow-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="sticky top-0 bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                                  Name
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                                  Mobile
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                                  Email
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                                  Notif Sent
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                                  Notif Datetime
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {paginatedFundContacts.map((contact, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {contact.fund_name || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {contact.fund_mobile || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {contact.fund_email || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm">
                                    {contact.fund_notif_sent ? (
                                      <Badge color="green">
                                        <span className="inline-flex items-center gap-1">
                                          <CheckCircleIcon className="h-5 w-5" />
                                          Yes
                                        </span>
                                      </Badge>
                                    ) : (
                                      <Badge color="red">
                                        <span className="inline-flex items-center gap-1">
                                          <XCircleIcon className="h-5 w-5" />
                                          No
                                        </span>
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {contact.fund_notif_datetime
                                      ? formatDate(
                                          contact.fund_notif_datetime,
                                          true
                                        )
                                      : "N/A"}
                                  </td>
                                </tr>
                              ))}
                              {paginatedFundContacts.length === 0 && (
                                <tr>
                                  <td colSpan={5} className="px-4 py-6">
                                    <EmptyState
                                      title={
                                        searchQuery
                                          ? "No matching fund contacts"
                                          : "No fund contacts"
                                      }
                                      subtitle={
                                        searchQuery
                                          ? "Try a different search term."
                                          : "There are currently no fund contacts."
                                      }
                                    />
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-col items-center justify-between gap-3 text-sm text-gray-600 md:flex-row">
                        <div>
                          Showing{" "}
                          {totalItems > 0
                            ? (currentPage - 1) * itemsPerPage + 1
                            : 0}{" "}
                          to {Math.min(currentPage * itemsPerPage, totalItems)}{" "}
                          of {totalItems} entries
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            First
                          </button>
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <span className="px-3 py-1.5 text-sm text-gray-700">
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Next
                          </button>
                          <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "members" && (
                <div className="col-span-1 md:col-span-2">
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          Corporate Member List
                        </h3>
                        <p className="text-xs text-gray-500">
                          Showing members under {formData.corp_code || resolvedCorpCode}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={fetchCorporateMembers}
                        disabled={memberLoading}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <ArrowPathIcon
                          className={clsx(
                            "h-4 w-4",
                            memberLoading && "animate-spin"
                          )}
                        />
                        Refresh
                      </button>
                    </div>

                    <div className="mb-3 overflow-x-auto pb-1">
                      <div className="inline-flex min-w-full flex-nowrap items-center gap-2 whitespace-nowrap">
                        {memberExportActions.map((action) => {
                          const Icon = action.Icon;
                          const isExporting = memberExporting === action.type;
                          return (
                            <button
                              key={action.type}
                              type="button"
                              onClick={() => handleMemberExport(action.type)}
                              disabled={Boolean(memberExporting)}
                              className={clsx(
                                HEADER_ACTION_BUTTON_BASE,
                                action.className,
                                Boolean(memberExporting) &&
                                  "cursor-not-allowed opacity-60 hover:translate-y-0"
                              )}
                            >
                              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                                {isExporting ? (
                                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Icon className="h-4 w-4" />
                                )}
                              </span>
                              <span className="whitespace-nowrap leading-none">
                                {isExporting ? "Exporting..." : action.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="relative w-full md:max-w-sm">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={memberSearchQuery}
                          onChange={(e) => setMemberSearchQuery(e.target.value)}
                          className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          placeholder="Search member name, ID, or corporate"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Show</span>
                        <select
                          value={memberItemsPerPage}
                          onChange={handleMemberItemsPerPageChange}
                          className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span className="text-xs text-gray-500">entries</span>
                      </div>
                    </div>

                    <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
                      <div className="max-h-[40vh] overflow-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="sticky top-0 bg-gray-50">
                            <tr>
                              {[
                                "Company",
                                "Employee ID",
                                "Member ID",
                                "Member Name",
                                "Birthday",
                                "Gender",
                                "Age",
                                "Member Type",
                                "Position",
                                "Plan",
                                "Contact Number",
                                "Status",
                                "Renewal Date",
                                "Expiry Date",
                                "Actions",
                              ].map((h) => (
                                <th
                                  key={h}
                                  className={clsx(
                                    "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                                    h === "Actions" &&
                                      "sticky right-0 z-20 border-l border-gray-200 bg-gray-50"
                                  )}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {memberLoading ? (
                              <tr>
                                <td colSpan={15} className="px-4 py-8">
                                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                                    <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-600" />
                                    Loading corporate members...
                                  </div>
                                </td>
                              </tr>
                            ) : memberRows.length === 0 ? (
                              <tr>
                                <td colSpan={15} className="px-4 py-6">
                                  <EmptyState
                                    title={
                                      memberSearchQuery
                                        ? "No matching members"
                                        : "No members available"
                                    }
                                    subtitle={
                                      memberSearchQuery
                                        ? "Try a different search term."
                                        : "No member records found for this corporate."
                                    }
                                  />
                                </td>
                              </tr>
                            ) : (
                              memberRows.map((member) => {
                                const memberType = formatMemberType(
                                  member.member_type
                                );
                                const status =
                                  memberStatusMap[
                                    normalizeMemberStatusFlag(member.status_flag)
                                  ] || memberStatusMap.default;
                                const rowKey = getMemberActionKey(member);
                                const actions = getMemberRowActions(member);

                                return (
                                  <tr
                                    key={rowKey}
                                    className="group hover:bg-blue-50/40"
                                  >
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.actual_corp_name ||
                                        member.corp_name ||
                                        member.corp_code ||
                                        "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.employee_id || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                      {member.member_id || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {formatMemberName(member) || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {formatDate(member.date_of_birth)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {formatGender(member.gender)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {calculateAge(member.date_of_birth)}
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                      <Badge color={memberType.color}>
                                        {memberType.label}
                                      </Badge>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.position || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.plan_name ||
                                        member.plan_code ||
                                        "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {member.mob_tel || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                      <Badge color={status.color}>
                                        {status.name}
                                      </Badge>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {formatDate(member.renewal_date)}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {formatDate(member.expiry_date)}
                                    </td>
                                    <td className="sticky right-0 z-10 border-l border-gray-100 bg-white px-4 py-2 text-sm shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.2)] group-hover:bg-blue-50/40">
                                      <div className="relative">
                                        <button
                                          type="button"
                                          aria-label={`Actions for member ${
                                            member.member_id || ""
                                          }`}
                                          onClick={(event) =>
                                            handleMemberActionMenuToggle(
                                              member,
                                              event
                                            )
                                          }
                                          className={clsx(
                                            "inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                                            openMemberActionMenu === rowKey
                                              ? "border-blue-400 bg-blue-50 text-blue-700 shadow-blue-100"
                                              : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                                          )}
                                        >
                                          <EllipsisVerticalIcon className="h-4 w-4 text-gray-500" />
                                          <span>Actions</span>
                                        </button>
                                        {openMemberActionMenu === rowKey &&
                                          memberActionMenuPosition &&
                                          typeof document !== "undefined" &&
                                          createPortal(
                                            <>
                                              <button
                                                type="button"
                                                aria-label="Close member actions menu"
                                                className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                                                onClick={closeMemberActionMenu}
                                              />
                                              <div
                                                className="fixed z-[9999] w-72 overflow-visible"
                                                style={{
                                                  top: `${memberActionMenuPosition.top}px`,
                                                  left: `${memberActionMenuPosition.left}px`,
                                                }}
                                              >
                                                <div
                                                  className={clsx(
                                                    "absolute right-6 h-3 w-3 rotate-45 border bg-white",
                                                    memberActionMenuPosition.placement ===
                                                      "top"
                                                      ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                                                      : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                                                  )}
                                                />
                                                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                                                  <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                      Member Actions
                                                    </p>
                                                  </div>
                                                  <div className="space-y-1 p-2">
                                                    {actions.map((action) => (
                                                      <button
                                                        key={action.key}
                                                        type="button"
                                                        onClick={() => {
                                                          action.onClick();
                                                          closeMemberActionMenu();
                                                        }}
                                                        className="group flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm"
                                                      >
                                                        <span className="flex min-w-0 items-center">
                                                          <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                                            <action.Icon
                                                              className={clsx(
                                                                "h-4 w-4",
                                                                action.color
                                                              )}
                                                            />
                                                          </span>
                                                          <span className="truncate font-medium">
                                                            {action.label}
                                                          </span>
                                                        </span>
                                                      </button>
                                                    ))}
                                                  </div>
                                                </div>
                                              </div>
                                            </>,
                                            document.body
                                          )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-col items-center justify-between gap-3 text-sm text-gray-600 md:flex-row">
                      <div>
                        Showing{" "}
                        {memberTotalCount > 0
                          ? (memberCurrentPage - 1) * memberItemsPerPage + 1
                          : 0}{" "}
                        to{" "}
                        {Math.min(
                          memberCurrentPage * memberItemsPerPage,
                          memberTotalCount
                        )}{" "}
                        of {memberTotalCount} entries
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleMemberPageChange(1)}
                          disabled={memberCurrentPage === 1 || memberLoading}
                          className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          First
                        </button>
                        <button
                          onClick={() =>
                            handleMemberPageChange(memberCurrentPage - 1)
                          }
                          disabled={memberCurrentPage === 1 || memberLoading}
                          className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1.5 text-sm text-gray-700">
                          Page {memberCurrentPage} of {memberTotalPages}
                        </span>
                        <button
                          onClick={() =>
                            handleMemberPageChange(memberCurrentPage + 1)
                          }
                          disabled={
                            memberCurrentPage === memberTotalPages ||
                            memberLoading
                          }
                          className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => handleMemberPageChange(memberTotalPages)}
                          disabled={
                            memberCurrentPage === memberTotalPages ||
                            memberLoading
                          }
                          className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
      {isMemberViewOpen && selectedMemberId && (
        <MemberViewModal
          memberId={selectedMemberId}
          onClose={() => {
            setIsMemberViewOpen(false);
            setSelectedMemberId(null);
          }}
        />
      )}
    </AnimatePresence>
  );
};

export default CorporateViewModal;
